// =============================================================================
// MeuDriver — Stats avançadas do instrutor ("Seus números")
// -----------------------------------------------------------------------------
// Cards exibindo:
//   * Taxa de retorno (alunos 2+ aulas / alunos distintos)
//   * Taxa de no-show (no_show / confirmados)
//   * Comparativo regional (AVG hourly_rate na cidade)
//   * Evolução do rating (últimos 6 meses)
//
// Faz queries client-side via supabase JS. Para escalas maiores, viraria RPC.
// =============================================================================

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { TrendingUp, Users2, AlertTriangle, MapPin, Star } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import type { BookingRow } from '@/types/database'

interface Props {
  instructorId: string
  hourlyRate: number | null
  city: string | null
  bookings: BookingRow[]
}

interface MonthlyAvg {
  monthKey: string // YYYY-MM
  label: string    // "Jan/25"
  avg: number | null
  count: number
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  tone = 'brand',
}: {
  icon: React.ElementType
  label: string
  value: string
  hint?: string
  tone?: 'brand' | 'teal' | 'orange' | 'emerald' | 'pink'
}) {
  const toneClasses: Record<string, string> = {
    brand: 'text-brand-600 bg-brand-50',
    teal: 'text-teal-700 bg-teal-50',
    orange: 'text-orange-600 bg-orange-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    pink: 'text-pink-600 bg-pink-50',
  }
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
              {label}
            </p>
            <p className="mt-1 text-xl font-bold text-gray-900">{value}</p>
            {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
          </div>
          <div className={`rounded-lg p-2 ${toneClasses[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export function InstructorAdvancedStats({ instructorId, hourlyRate, city, bookings }: Props) {
  // Comparativo regional: AVG hourly_rate de instrutores aprovados da mesma cidade.
  const { data: regionAvg, isLoading: regionLoading } = useQuery({
    queryKey: ['region-avg-rate', city],
    enabled: !!city,
    queryFn: async (): Promise<number | null> => {
      // Filtra por city via JOIN com profile. Como não suporta agregação direta
      // por cidade no PostgREST, fazemos client-side com paginação razoável.
      const { data } = await supabase
        .from('instructors')
        .select('hourly_rate, profile:profiles!instructors_profile_id_fkey(city)')
        .eq('status', 'approved')
        .not('hourly_rate', 'is', null)
        .limit(500)
      if (!data) return null
      const same = (data as unknown as Array<{ hourly_rate: number | null; profile: { city: string | null } | null }>)
        .filter((r) => (r.profile?.city ?? '').toLowerCase() === (city ?? '').toLowerCase())
        .map((r) => r.hourly_rate ?? 0)
        .filter((v) => v > 0)
      if (same.length === 0) return null
      return same.reduce((a, b) => a + b, 0) / same.length
    },
  })

  // Evolução do rating: últimos 6 meses agrupados.
  const [ratingHistory, setRatingHistory] = useState<MonthlyAvg[] | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      const since = new Date()
      since.setMonth(since.getMonth() - 6)
      since.setDate(1)
      since.setHours(0, 0, 0, 0)
      const { data } = await supabase
        .from('reviews')
        .select('rating, created_at')
        .eq('instructor_id', instructorId)
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: true })
      if (cancelled) return
      const buckets: Record<string, { sum: number; count: number; label: string }> = {}
      const monthLabels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
      for (let i = 5; i >= 0; i--) {
        const d = new Date()
        d.setDate(1)
        d.setMonth(d.getMonth() - i)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        buckets[key] = {
          sum: 0,
          count: 0,
          label: `${monthLabels[d.getMonth()]}/${String(d.getFullYear()).slice(-2)}`,
        }
      }
      for (const r of (data ?? []) as Array<{ rating: number; created_at: string }>) {
        const d = new Date(r.created_at)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        if (buckets[key]) {
          buckets[key].sum += r.rating
          buckets[key].count += 1
        }
      }
      const arr: MonthlyAvg[] = Object.entries(buckets).map(([k, v]) => ({
        monthKey: k,
        label: v.label,
        avg: v.count > 0 ? v.sum / v.count : null,
        count: v.count,
      }))
      setRatingHistory(arr)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [instructorId])

  // Cálculo client-side das taxas a partir dos bookings já carregados.
  const distinctStudents = new Set(bookings.map((b) => b.student_id))
  const studentCounts: Record<string, number> = {}
  for (const b of bookings) {
    if (b.status === 'completed' || b.status === 'confirmed' || b.status === 'in_progress' || b.status === 'no_show') {
      studentCounts[b.student_id] = (studentCounts[b.student_id] ?? 0) + 1
    }
  }
  const recurring = Object.values(studentCounts).filter((c) => c >= 2).length
  const returnRate = distinctStudents.size > 0 ? (recurring / distinctStudents.size) * 100 : 0

  const confirmedOrCompletedOrNoShow = bookings.filter((b) =>
    b.status === 'completed' || b.status === 'no_show' || b.status === 'in_progress' || b.status === 'confirmed',
  )
  const noShowCount = bookings.filter((b) => b.status === 'no_show').length
  const noShowRate = confirmedOrCompletedOrNoShow.length > 0
    ? (noShowCount / confirmedOrCompletedOrNoShow.length) * 100
    : 0

  // Comparativo com mediana regional
  let regionHint = 'Sem dados de outros instrutores na cidade.'
  if (typeof regionAvg === 'number' && hourlyRate && hourlyRate > 0) {
    const diff = ((hourlyRate - regionAvg) / regionAvg) * 100
    if (diff > 5) regionHint = `Você cobra ${diff.toFixed(0)}% acima da média.`
    else if (diff < -5) regionHint = `Você cobra ${Math.abs(diff).toFixed(0)}% abaixo da média.`
    else regionHint = `Você está alinhado com a média.`
  }

  // Evolução do rating: último vs anterior
  const ratingLatest = ratingHistory?.findLast?.((m) => m.avg !== null)
  const ratingPrev = ratingHistory?.slice(0, ratingHistory.indexOf(ratingLatest!)).findLast?.((m) => m.avg !== null)
  let ratingHint = '—'
  if (ratingLatest && ratingLatest.avg !== null) {
    if (ratingPrev && ratingPrev.avg !== null) {
      const delta = ratingLatest.avg - ratingPrev.avg
      const arrow = delta > 0.05 ? '↑' : delta < -0.05 ? '↓' : '→'
      ratingHint = `${arrow} de ${ratingPrev.avg.toFixed(1)} para ${ratingLatest.avg.toFixed(1)}`
    } else {
      ratingHint = `Em ${ratingLatest.label}`
    }
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-gray-900">Seus números</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users2}
          label="Taxa de retorno"
          value={`${returnRate.toFixed(0)}%`}
          hint={`${recurring} de ${distinctStudents.size} alunos voltaram`}
          tone="teal"
        />
        <StatCard
          icon={AlertTriangle}
          label="Taxa de no-show"
          value={`${noShowRate.toFixed(1)}%`}
          hint={`${noShowCount} faltas em ${confirmedOrCompletedOrNoShow.length} aulas`}
          tone="orange"
        />
        <StatCard
          icon={MapPin}
          label="Média da região"
          value={
            regionLoading
              ? '—'
              : regionAvg
              ? formatCurrency(regionAvg)
              : '—'
          }
          hint={regionHint}
          tone="brand"
        />
        <StatCard
          icon={Star}
          label="Rating no mês"
          value={
            ratingLatest && ratingLatest.avg !== null ? ratingLatest.avg.toFixed(2) : '—'
          }
          hint={ratingHint}
          tone="emerald"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-brand-600" />
            Evolução do rating (6 meses)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ratingHistory === null ? (
            <Skeleton className="h-32" />
          ) : (
            <RatingMiniChart history={ratingHistory} />
          )}
        </CardContent>
      </Card>
    </section>
  )
}

function RatingMiniChart({ history }: { history: MonthlyAvg[] }) {
  // SVG simples: linha de ratings (0-5) com pontos.
  const w = 360
  const h = 120
  const padL = 28
  const padR = 12
  const padT = 8
  const padB = 24
  const innerW = w - padL - padR
  const innerH = h - padT - padB

  const points = history.map((m, i) => {
    const x = padL + (i / Math.max(history.length - 1, 1)) * innerW
    const y = m.avg !== null
      ? padT + (1 - m.avg / 5) * innerH
      : null
    return { x, y, label: m.label, count: m.count, avg: m.avg }
  })

  const pathD = points
    .filter((p) => p.y !== null)
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y!.toFixed(1)}`)
    .join(' ')

  return (
    <div className="overflow-x-auto">
      <svg width={w} height={h} role="img" aria-label="Gráfico de evolução do rating">
        {/* Grid horizontal */}
        {[0, 1, 2, 3, 4, 5].map((v) => {
          const y = padT + (1 - v / 5) * innerH
          return (
            <g key={v}>
              <line
                x1={padL}
                y1={y}
                x2={w - padR}
                y2={y}
                stroke="#f3f4f6"
                strokeWidth={1}
              />
              <text x={padL - 6} y={y + 3} fontSize={9} fill="#9ca3af" textAnchor="end">
                {v}
              </text>
            </g>
          )
        })}

        {/* Linha do path */}
        {pathD && (
          <path
            d={pathD}
            fill="none"
            stroke="#0d9488"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {/* Pontos */}
        {points.map((p, i) => (
          <g key={i}>
            {p.y !== null && (
              <circle cx={p.x} cy={p.y} r={3.5} fill="#0d9488">
                <title>
                  {p.label}: {p.avg?.toFixed(2)} ({p.count} avaliação{p.count !== 1 ? 'es' : ''})
                </title>
              </circle>
            )}
            <text
              x={p.x}
              y={h - padB + 14}
              fontSize={9}
              fill="#6b7280"
              textAnchor="middle"
            >
              {p.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  )
}
